import { useEffect, useRef, useState } from "react";

/**
 * Gmail-style undo toast — the SOTA-recommended replacement for confirm
 * modals on low-stakes frequent actions (NN-g "UX for Reversible Actions"
 * Dec 2025). The canonical reference is Gmail's "Conversation moved to
 * Trash. Undo" bar: simple flat toast + Undo link + countdown progress.
 *
 * Contract pinned in __tests__/JobUndoToast.test.tsx:
 *   * Render message + Undo button
 *   * Click Undo → onUndo() once
 *   * Click Undo → onTimeout NEVER fires (firedRef guard + clearInterval)
 *   * No click → onTimeout fires exactly once after durationMs
 *   * Progress bar uses transform: scaleX(...) — NOT width animation —
 *     because transform is GPU-accelerated and avoids per-tick layout
 *     reflow. Bar lives at the bottom of the toast, origin-left so it
 *     visually drains from full → empty as remaining time decreases.
 *   * role=status + aria-live=polite — announce-without-interrupt
 *     (assertive would steal focus; off would hide entirely)
 *
 * Implementation notes:
 *   * setInterval(100ms) — NOT setTimeout(durationMs) — because the
 *     progress bar needs intermediate updates. 100ms is the sweet spot
 *     between visual smoothness (~10 fps for the bar; the browser still
 *     interpolates the transform between frames) and React commit cost.
 *   * firedRef is the structural floor preventing double-fire: even if a
 *     pending state-update commit races with a click, the guard short-
 *     circuits both paths. Without it, an Undo click landing on the same
 *     tick as the timeout fire could fire BOTH callbacks. The interval
 *     is also cleared on Undo to stop subsequent ticks from doing wasted
 *     re-renders.
 */

interface JobUndoToastProps {
  message: string;
  onUndo: () => void;
  onTimeout: () => void;
  durationMs?: number;
}

const TICK_MS = 100;
const DEFAULT_DURATION_MS = 5_000;

export function JobUndoToast({
  message,
  onUndo,
  onTimeout,
  durationMs = DEFAULT_DURATION_MS,
}: JobUndoToastProps) {
  const [elapsed, setElapsed] = useState(0);
  // firedRef: structural guard ensuring onTimeout fires AT MOST ONCE and
  // Undo can suppress it even on the same-tick race. See contract docs above.
  const firedRef = useRef(false);
  // intervalRef: cleared on unmount AND on Undo click so the toast stops
  // doing per-100ms re-renders after the operator has decided.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + TICK_MS;
        if (next >= durationMs && !firedRef.current) {
          firedRef.current = true;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onTimeout();
        }
        return next;
      });
    }, TICK_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [durationMs, onTimeout]);

  const handleUndo = () => {
    // Suppress any in-flight or future onTimeout fire BEFORE invoking
    // onUndo. firedRef must flip first so any concurrent tick callback
    // already mid-execution observes the guard and short-circuits.
    firedRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onUndo();
  };

  // ratio drains from 1 → 0 as elapsed climbs to durationMs. Clamped at
  // both ends so the bar never overshoots even if elapsed grows past
  // durationMs in some edge case.
  const remaining = Math.max(0, durationMs - elapsed);
  const ratio = Math.min(1, remaining / durationMs);

  return (
    <div
      role="status"
      aria-live="polite"
      className="bk-anim-message fixed bottom-6 left-1/2 z-50 flex min-w-[280px] max-w-[400px] -translate-x-1/2 items-center gap-3 overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-raised)] px-4 py-3 shadow-[var(--elev-whisper-lg)] ring-1 ring-[var(--border-default)]"
    >
      <span className="type-body-sm flex-1 text-[var(--text-primary)]">{message}</span>
      <button
        type="button"
        onClick={handleUndo}
        aria-label="Undo"
        className="type-body-sm rounded-[var(--radius-pill)] px-3 py-1 font-medium text-[var(--accent-text)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--accent-soft)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
      >
        Undo
      </button>
      {/* Progress bar: scaleX from 1 → 0; transform-origin: left so it
          drains leftward. aria-hidden because the message+role=status
          above already conveys the deletion to assistive tech — the
          countdown is a visual affordance, not semantic content. */}
      <div
        data-testid="undo-progress-bar"
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-full origin-left bg-[var(--accent-primary)]"
        style={{ transform: `scaleX(${ratio})` }}
      />
    </div>
  );
}
